import React, { useState, useEffect, useRef, useCallback } from "react";
import { Play, Pause } from "lucide-react";

const PrompterTemplate = ({
  text = "",
  font = "Helvetica",
  fontSize = 23,
  isBold = false,
  isItalic = false,
  textColor = "#000000",
  speed = 0,
  layout = "compact",
  theme = "light",
  textAlign = "left",
  preview = false,
  inmeeting = false,
  onClose,
  position = "center",
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const previewRef = useRef(null);
  const previewContentRef = useRef(null);
  const scrollIntervalRef = useRef(null);
  const hasStartedPlayingRef = useRef(false);
  const pausedScrollPositionRef = useRef(0);

  // Reset play state when text changes
  useEffect(() => {
    hasStartedPlayingRef.current = false;
    pausedScrollPositionRef.current = 0;
    if (isPlaying) {
      setIsPlaying(false);
      if (scrollIntervalRef.current) {
        clearInterval(scrollIntervalRef.current);
        scrollIntervalRef.current = null;
      }
    }
    // Reset padding when text changes
    if (previewContentRef.current) {
      previewContentRef.current.style.paddingTop = "2rem";
      previewContentRef.current.style.paddingBottom = "2rem";
    }
    // Reset scroll position when text changes
    // Note: Positioning first line at bottom is handled in separate useEffect
    if (previewRef.current && !inmeeting) {
      previewRef.current.scrollTop = 0;
    }
  }, [text, inmeeting]);

  // Position first line at bottom when component mounts in meeting mode
  useEffect(() => {
    if (inmeeting && !isPlaying) {
      const positionFirstLineAtBottom = () => {
        if (previewRef.current && previewContentRef.current && !isPlaying) {
          const containerHeight = previewRef.current.clientHeight;
          if (containerHeight > 0) {
            // Measure actual rendered line height from the text element
            const textElement = previewContentRef.current.querySelector("p");
            let actualLineHeight = fontSize * 1.7; // Default estimate

            if (textElement) {
              const computedStyle = window.getComputedStyle(textElement);
              const fontSizeValue =
                parseFloat(computedStyle.fontSize) || fontSize;
              const lineHeightValue = computedStyle.lineHeight;

              // Parse line height (could be 'normal', number, or px value)
              if (
                lineHeightValue !== "normal" &&
                !isNaN(parseFloat(lineHeightValue))
              ) {
                if (lineHeightValue.includes("px")) {
                  actualLineHeight = parseFloat(lineHeightValue);
                } else {
                  // If it's a unitless number, multiply by font size
                  actualLineHeight =
                    parseFloat(lineHeightValue) * fontSizeValue;
                }
              } else {
                // 'normal' line-height, use font size with multiplier
                actualLineHeight = fontSizeValue * 1.7;
              }
            }

            // To position first line at bottom when scrollTop = 0:
            // Set paddingTop to push first line down to bottom of visible container
            // paddingTop should be containerHeight - one line height
            const paddingTop = Math.max(
              containerHeight - actualLineHeight - 30,
              containerHeight * 0.9
            );
            previewContentRef.current.style.paddingTop = `${paddingTop}px`;
            previewContentRef.current.style.paddingBottom = "2rem";

            // Force scroll to top so first line appears at bottom
            previewRef.current.scrollTop = 0;

            // Verify position after layout stabilizes
            requestAnimationFrame(() => {
              if (
                previewRef.current &&
                previewContentRef.current &&
                !isPlaying
              ) {
                previewRef.current.scrollTop = 0;
              }
            });
          }
        }
      };

      // Use multiple approaches to ensure it runs after layout
      const timer1 = setTimeout(() => {
        positionFirstLineAtBottom();
      }, 50);

      const timer2 = setTimeout(() => {
        requestAnimationFrame(() => {
          requestAnimationFrame(positionFirstLineAtBottom);
        });
      }, 200);

      // Also handle window resize to reposition first line at bottom
      const handleResize = () => {
        if (!isPlaying) {
          positionFirstLineAtBottom();
        }
      };
      window.addEventListener("resize", handleResize);

      return () => {
        clearTimeout(timer1);
        clearTimeout(timer2);
        window.removeEventListener("resize", handleResize);
      };
    }
  }, [inmeeting, fontSize, isPlaying, text]);

  // Handle play/pause
  const handlePlayPause = useCallback(() => {
    if (isPlaying) {
      // Pause: stop scrolling and hold at current position
      if (scrollIntervalRef.current) {
        clearInterval(scrollIntervalRef.current);
        scrollIntervalRef.current = null;
      }
      // Store the exact current scroll position before pausing
      if (previewRef.current) {
        const currentScroll = previewRef.current.scrollTop;
        pausedScrollPositionRef.current = currentScroll;
        // Immediately lock the position
        previewRef.current.scrollTop = currentScroll;
      }
      setIsPlaying(false);
    } else {
      // Play: resume from current position - NEVER reset scrollTop on resume
      // The scroll position should always be preserved when resuming
      if (previewRef.current) {
        // Only set up on the very first play (when hasStartedPlayingRef is false)
        if (!hasStartedPlayingRef.current) {
          hasStartedPlayingRef.current = true;
          // On first play only, ensure padding is set for marquee effect
          if (previewContentRef.current && inmeeting) {
            const containerHeight = previewRef.current.clientHeight;
            if (containerHeight > 0) {
              previewContentRef.current.style.paddingTop = `${containerHeight}px`;
              previewContentRef.current.style.paddingBottom = `${containerHeight}px`;
            }
          }
          // Only reset to start on the very first play, when component first mounts
          // After that, always preserve current scroll position
          previewRef.current.scrollTop = 0;
        }
        // When resuming (hasStartedPlayingRef is already true), do NOT touch scrollTop
        // Just resume playing from wherever we paused
        setIsPlaying(true);
      }
    }
  }, [isPlaying, inmeeting]);

  // Handle manual scroll with arrow keys
  const handleManualScroll = useCallback(
    (direction) => {
      if (previewRef.current) {
        const scrollAmount = 50; // pixels to scroll per keypress
        let newScrollTop;
        if (direction === "up") {
          newScrollTop = Math.max(
            0,
            previewRef.current.scrollTop - scrollAmount
          );
          previewRef.current.scrollTop = newScrollTop;
        } else if (direction === "down") {
          const maxScroll =
            previewRef.current.scrollHeight - previewRef.current.clientHeight;
          newScrollTop = Math.min(
            maxScroll,
            previewRef.current.scrollTop + scrollAmount
          );
          previewRef.current.scrollTop = newScrollTop;
        }
        // Update the locked position if paused, so manual scroll becomes the new locked position
        if (!isPlaying) {
          pausedScrollPositionRef.current = newScrollTop;
        }
      }
    },
    [isPlaying]
  );

  // Keyboard event handlers for meeting mode
  useEffect(() => {
    if (!inmeeting) return;

    const handleKeyDown = (e) => {
      // Spacebar: play/pause - hold at current position when paused
      if (e.code === "Space" && speed > 0) {
        e.preventDefault();
        // Store current scroll position before toggling
        const currentScroll = previewRef.current?.scrollTop || 0;
        const wasPlaying = isPlaying;
        handlePlayPause();
        // If pausing (was playing), ensure we hold the exact position
        if (wasPlaying && previewRef.current) {
          // Force scroll to the stored position to hold it
          requestAnimationFrame(() => {
            if (previewRef.current) {
              previewRef.current.scrollTop = currentScroll;
            }
          });
        }
      }
      // Arrow Up: scroll up
      else if (e.key === "ArrowUp") {
        e.preventDefault();
        // If playing, pause first to hold position, then scroll
        if (isPlaying) {
          // Store current position before pausing
          const currentScroll = previewRef.current?.scrollTop || 0;
          pausedScrollPositionRef.current = currentScroll;
          setIsPlaying(false);
          if (scrollIntervalRef.current) {
            clearInterval(scrollIntervalRef.current);
            scrollIntervalRef.current = null;
          }
        }
        handleManualScroll("up");
      }
      // Arrow Down: scroll down
      else if (e.key === "ArrowDown") {
        e.preventDefault();
        // If playing, pause first to hold position, then scroll
        if (isPlaying) {
          // Store current position before pausing
          const currentScroll = previewRef.current?.scrollTop || 0;
          pausedScrollPositionRef.current = currentScroll;
          setIsPlaying(false);
          if (scrollIntervalRef.current) {
            clearInterval(scrollIntervalRef.current);
            scrollIntervalRef.current = null;
          }
        }
        handleManualScroll("down");
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [inmeeting, isPlaying, speed, handlePlayPause, handleManualScroll]);

  // Effect to handle scrolling animation and lock position when paused
  useEffect(() => {
    // When paused, clear any existing interval and actively hold position
    if (!isPlaying) {
      if (scrollIntervalRef.current) {
        clearInterval(scrollIntervalRef.current);
        scrollIntervalRef.current = null;
      }

      // Store the current scroll position when paused
      // Always update it to ensure we have the latest position
      if (previewRef.current) {
        pausedScrollPositionRef.current = previewRef.current.scrollTop;
      }

      // Lock the scroll position when paused
      // Use a scroll event listener to prevent any drift
      const handleScrollLock = () => {
        if (previewRef.current && !isPlaying) {
          const lockedPosition = pausedScrollPositionRef.current;
          if (Math.abs(previewRef.current.scrollTop - lockedPosition) > 1) {
            // If scroll position has changed, restore it immediately
            previewRef.current.scrollTop = lockedPosition;
          }
        }
      };

      if (previewRef.current) {
        // Set initial position
        previewRef.current.scrollTop = pausedScrollPositionRef.current;

        // Listen for scroll events to lock position
        previewRef.current.addEventListener("scroll", handleScrollLock, {
          passive: false,
        });

        // Also use requestAnimationFrame to continuously lock position
        let lockFrameId;
        const lockPosition = () => {
          if (previewRef.current && !isPlaying) {
            previewRef.current.scrollTop = pausedScrollPositionRef.current;
            lockFrameId = requestAnimationFrame(lockPosition);
          }
        };
        lockFrameId = requestAnimationFrame(lockPosition);

        return () => {
          previewRef.current?.removeEventListener("scroll", handleScrollLock);
          if (lockFrameId) {
            cancelAnimationFrame(lockFrameId);
          }
        };
      }

      return;
    }

    if (isPlaying && previewRef.current && speed > 0) {
      // Calculate scroll speed for smoother animation
      // Speed is 0-100, convert to pixels per second, then divide by frames per second
      // Using smaller increments with more frequent updates for smoother scrolling
      const pixelsPerSecond = (speed / 100) * 200; // Max 200px per second at speed 100
      const fps = 60; // 60 frames per second for smooth animation
      const scrollAmount = pixelsPerSecond / fps; // Pixels per frame
      const intervalTime = 1000 / fps; // ~16.67ms for 60fps

      // Set initial padding when starting to play for marquee effect
      // Top padding positions first line at bottom, bottom padding allows last line to reach top
      // Only set padding if not already set (first time playing)
      if (previewRef.current && previewContentRef.current) {
        const containerHeight = previewRef.current.clientHeight;
        const currentPaddingTop = previewContentRef.current.style.paddingTop;
        const currentPaddingTopValue = parseFloat(currentPaddingTop) || 0;

        // Only set padding if it's not already set for marquee effect (first time playing)
        // Check if padding is less than container height (means not set for marquee yet)
        if (
          !currentPaddingTop ||
          currentPaddingTop === "2rem" ||
          currentPaddingTop === "1rem" ||
          currentPaddingTop === "" ||
          (inmeeting && currentPaddingTopValue < containerHeight * 0.8)
        ) {
          previewContentRef.current.style.paddingTop = `${containerHeight}px`;
          previewContentRef.current.style.paddingBottom = `${containerHeight}px`;
          // Only reset scrollTop on the very first play, not on resume
          if (!hasStartedPlayingRef.current) {
            previewRef.current.scrollTop = 0;
            hasStartedPlayingRef.current = true;
          }
        }
      }

      scrollIntervalRef.current = setInterval(() => {
        if (previewRef.current) {
          const currentScroll = previewRef.current.scrollTop;
          const maxScroll =
            previewRef.current.scrollHeight - previewRef.current.clientHeight;

          if (currentScroll >= maxScroll) {
            // Last line has reached the top, loop back to start (infinite marquee)
            previewRef.current.scrollTop = 0;
          } else {
            // Scroll down (increase scrollTop) - content moves up, creating marquee effect
            // Use smaller increments for smoother scrolling
            previewRef.current.scrollTop = Math.min(
              maxScroll,
              currentScroll + scrollAmount
            );
          }
        }
      }, intervalTime);
    }

    // Cleanup on unmount
    return () => {
      if (scrollIntervalRef.current) {
        clearInterval(scrollIntervalRef.current);
      }
    };
  }, [isPlaying, speed]);

  const containerClass = `
    ${layout === "wide" ? "max-w-4xl" : "max-w-md"}
    ${theme === "dark" ? "bg-gray-900" : "bg-white"}
    ${inmeeting ? "rounded-lg shadow-lg" : "rounded-lg shadow-lg"}
    ${preview ? "w-full" : ""}
    ${inmeeting ? "w-full max-w-6xl mx-auto" : ""}
    ${inmeeting ? "h-[calc(100vh-130px)]" : ""}
    relative
    flex flex-col
  `;

  const textStyle = {
    fontFamily: font,
    fontSize: `${fontSize}px`,
    color: textColor,
    fontWeight: isBold ? "bold" : "normal",
    fontStyle: isItalic ? "italic" : "normal",
    textAlign: textAlign || "left",
    lineHeight: `${fontSize * 1.7}px`,
    letterSpacing: "0.01em",
  };

  return (
    <div className={containerClass.trim()}>
      {/* Scrollable preview area */}
      <div
        ref={previewRef}
        className={`flex-1 overflow-y-auto ${
          theme === "dark" ? "bg-gray-900" : "bg-white"
        } ${inmeeting ? "" : "flex items-center justify-center"}`}
        style={{
          scrollBehavior: isPlaying ? "auto" : "smooth",
          minHeight: inmeeting ? "auto" : "300px",
          maxHeight: inmeeting ? "calc(100vh - 140px)" : "400px",
          height: inmeeting ? "calc(100% - 60px)" : "auto",
        }}
      >
        <div
          ref={previewContentRef}
          style={{
            paddingTop: inmeeting ? "2rem" : "2rem", // Will be overridden by positioning effect in meeting mode
            paddingBottom: "2rem",
            paddingLeft: inmeeting ? "4rem" : "1rem",
            paddingRight: inmeeting ? "4rem" : "1rem",
            maxWidth: inmeeting ? "1200px" : "100%",
            width: "100%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-start",
            minHeight: inmeeting ? "100%" : "auto",
          }}
        >
          <p
            className={`leading-relaxed whitespace-pre-wrap ${
              inmeeting
                ? "text-2xl md:text-3xl lg:text-4xl"
                : "text-sm md:text-base"
            }`}
            style={{
              ...textStyle,
              textAlign: inmeeting ? "center" : textStyle.textAlign,
            }}
          >
            {text || "Type your script here..."}
          </p>
        </div>
      </div>

      {/* Bottom instruction bar for meeting mode */}
      {inmeeting && (
        <div className="bg-black bg-opacity-70 text-gray-300 px-4 py-3 flex items-center justify-between text-sm h-[120px]">
          <span>
            Press{" "}
            <kbd className="px-2 py-1 bg-gray-700 rounded text-white">
              SPACE BAR
            </kbd>{" "}
            to start/stop auto-scroll, or use{" "}
            <kbd className="px-2 py-1 bg-gray-700 rounded text-white">↑</kbd>{" "}
            <kbd className="px-2 py-1 bg-gray-700 rounded text-white">↓</kbd>{" "}
            arrows to move manually
          </span>
          {onClose && (
            <button
              onClick={onClose}
              className="text-white hover:text-gray-300 transition-colors ml-4 p-1"
              aria-label="Close"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default PrompterTemplate;
